import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { Alert, ScrollView, StyleSheet, Text, TouchableOpacity, View } from '../components/rn';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import ActivityIndicator from '../components/LoadingIndicator';
import MeetupDateTimeField from '../components/MeetupDateTimeField';
import { formatNosubjectCalendarEventWhen } from '../calendar/eventPresentation';
import { reservationStatusKey } from '../calendar/reservationStatus';
import {
  canAcceptReservationReschedule,
  canCancelReservation,
  canProposeReservationReschedule,
} from '../calendar/reservationActions';
import { confirmCalendarConflict } from '../calendar/conflictPrompt';
import { syncAcceptedMeetup } from '../calendar/acceptedMeetupSync';
import { removeMeetupFromSystemCalendar } from '../calendar/systemCalendar';
import { callsEnabled } from '../calls/config';
import { mediaAvailable } from '../calls/media';
import { formatMoney } from '../data/currencies';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import type { Meetup } from '../services/api';
import { api, ApiError } from '../services/api';
import { fontSize, spacing, type ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import SectionActionRow from '../components/SectionActionRow';
import { getDeviceTimeZone } from '../utils/locale';

type Props = {
  token: string;
  myId: number;
  meetupId: number;
  onBack: () => void;
  onStartCall?: (reservation: Meetup) => void;
};

type DetailRowProps = {
  label: string;
  value: string;
  testID?: string;
  styles: ReturnType<typeof makeStyles>;
};

function DetailRow({ label, value, testID, styles }: DetailRowProps) {
  return (
    <View style={styles.detailRow} testID={testID}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailValue} testID={testID ? `${testID}-value` : undefined}>{value}</Text>
    </View>
  );
}

// 9.31.7 — sonuç OLGUSAL dille sunulur: "arama yapılmadı" denir, "karşı taraf
// gelmedi" denmez. İkincisi bir iddia olurdu ve henüz verilmemiş bir ürün/hukuk
// kararını ima ederdi.
function outcomeKey(outcome: Meetup['call_outcome']) {
  if (outcome === 'held') return 'calendar.reservationHeld' as const;
  if (outcome === 'not_held') return 'calendar.reservationNotHeld' as const;
  return null;
}

function cancellationNotificationKey(
  state: NonNullable<Meetup['cancellation_notification']>['state'],
) {
  if (state === 'provider_accepted') return 'calendar.reservationNotificationProviderAccepted' as const;
  if (state === 'suppressed') return 'calendar.reservationNotificationSuppressed' as const;
  if (state === 'unavailable') return 'calendar.reservationNotificationUnavailable' as const;
  return 'calendar.reservationNotificationPending' as const;
}

function clock(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function initialRescheduleDate(meetup: Meetup, serverNowMs: number): Date {
  // Aynı zamanı yeniden gönderen geçersiz bir form açmayız; öneri ilk anda
  // mevcut başlangıçtan bir saat sonrasını gösterir, kullanıcı değiştirebilir.
  const current = new Date(new Date(meetup.proposed_at).getTime() + 60 * 60_000);
  const minimum = new Date(serverNowMs + 5 * 60_000);
  return current > minimum ? current : minimum;
}

export default function ReservationDetailScreen({
  token,
  myId,
  meetupId,
  onBack,
  onStartCall,
}: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const deviceTimeZone = useMemo(() => getDeviceTimeZone() ?? 'UTC', []);
  const [meetup, setMeetup] = useState<Meetup | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(() => new Date());
  const [rescheduleDateValid, setRescheduleDateValid] = useState(true);
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);
  const meetupRef = useRef<Meetup | null>(null);
  const loadRequestRef = useRef(0);
  const actionBusyRef = useRef(false);

  const load = useCallback(async (clearCurrent = false): Promise<Meetup | null> => {
    const requestId = ++loadRequestRef.current;
    setFailed(false);
    if (clearCurrent) {
      meetupRef.current = null;
      setMeetup(null);
    } else if (meetupRef.current) {
      setRefreshing(true);
    }
    try {
      const requestStartedAt = Date.now();
      const current = await api.getMeetup(token, meetupId);
      const responseReceivedAt = Date.now();
      if (requestId !== loadRequestRef.current) return null;
      const serverNow = current.server_now ? new Date(current.server_now).getTime() : NaN;
      if (Number.isFinite(serverNow)) {
        // NTP'nin basit orta-nokta yaklaşımı ağ gidiş/dönüş süresini iki yana
        // paylaştırır; cihaz saati dakikalarca yanlış olsa da sınır doğru yenilenir.
        setServerClockOffsetMs(serverNow - (requestStartedAt + responseReceivedAt) / 2);
      }
      meetupRef.current = current;
      setMeetup(current);
      return current;
    } catch {
      if (requestId === loadRequestRef.current) setFailed(true);
      return null;
    } finally {
      if (requestId === loadRequestRef.current) setRefreshing(false);
    }
  }, [meetupId, token]);

  useEffect(() => {
    setShowReschedule(false);
    load(true);
  }, [load]);

  // Başka cihazdaki değişiklikler ön plana dönüşte kanonik kayıttan uzlaştırılır.
  // Bir mutasyon sürerken ikinci okuma başlatılmaz; başarılı mutasyon kendi
  // kanonik okumasını tamamlamadan eylem kilidini bırakmaz.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active' && !actionBusyRef.current) load(false);
    });
    return () => subscription.remove();
  }, [load]);

  // Uzun süre açık bırakılmış ayrıntıda çağrı penceresi ve rezervasyon bitişi
  // geldiğinde kanonik kayıt yeniden okunur. Böylece "Şimdi ara" kadar yeni
  // saat eylemi de eski ekran yüzünden süresi geçtikten sonra açık kalmaz.
  // Sunucu mutasyon rotaları yine nihai kapıdır.
  useEffect(() => {
    if (!meetup || meetup.status !== 'accepted') return;
    const estimatedServerNow = Date.now() + serverClockOffsetMs;
    const reservationEndsAt = new Date(meetup.proposed_at).getTime()
      + Math.max(1, meetup.duration_minutes) * 60_000;
    const callBoundary = meetup.kind === 'call'
      ? meetup.call_link_state === 'too_early'
        ? meetup.call_link_opens_at
        : meetup.call_link_state === 'available'
          ? meetup.call_link_closes_at
          : null
      : null;
    const boundary = [reservationEndsAt, callBoundary ? new Date(callBoundary).getTime() : NaN]
      .filter((value) => Number.isFinite(value) && value > estimatedServerNow)
      .sort((a, b) => a - b)[0];
    if (boundary === undefined) return;
    const delay = boundary - estimatedServerNow + 1_000;
    if (!Number.isFinite(delay) || delay <= 0 || delay > 2_147_000_000) return;
    const timer = setTimeout(() => {
      if (!actionBusyRef.current) load(false);
    }, delay);
    return () => clearTimeout(timer);
  }, [load, meetup, serverClockOffsetMs]);

  // 7.14 · D-550 — başlık içeriğin üstünde yüzüyor.
  const ustBosluk = screenHeaderContentInset(insets.top);
  const header = (
    <PageHeader title={t('calendar.reservationDetails')} onBack={onBack} floating />
  );

  if (!meetup) {
    return (
      <View style={styles.container}>
        {header}
        <View style={styles.loadState}>
          {failed ? (
            <>
              <Text style={styles.loadError}>{t('common.error')}</Text>
              <TouchableOpacity
                accessibilityRole="button"
                onPress={() => { load(true); }}
                style={styles.retryButton}>
                <Text style={styles.retryText}>{t('common.retry')}</Text>
              </TouchableOpacity>
            </>
          ) : <ActivityIndicator />}
        </View>
      </View>
    );
  }
  // React state could change after this render; action closures use the exact
  // canonical snapshot whose record_revision was shown with these controls.
  const reservation = meetup;
  // Sunucunun kanonik ayrıntıda verdiği an, yalnız çağrı düğmesini değil bu
  // kaydın bütün zamana bağlı eylemlerini yönetir. Böylece cihaz saati ileri
  // ya da geri olduğunda yeni saat eylemi yanlışlıkla gizlenmez/açılmaz.
  const estimatedServerNowMs = Date.now() + serverClockOffsetMs;

  const reservationWhen = formatNosubjectCalendarEventWhen({
    proposedAt: reservation.proposed_at,
    durationMinutes: reservation.duration_minutes,
    sourceTimeZone: reservation.source_time_zone,
  }, lang, deviceTimeZone);
  const total = reservation.total_amount == null && reservation.pricing_frozen === false
    ? '—'
    : formatMoney(
      reservation.total_amount == null
        ? Number(reservation.recipient_price ?? reservation.recipient_price_time) * reservation.duration_minutes
        : Number(reservation.total_amount),
      reservation.price_currency ?? reservation.recipient_currency,
      lang,
    );
  const rate = reservation.price_per_min == null && reservation.pricing_frozen === false
    ? '—'
    : formatMoney(
      Number(reservation.price_per_min ?? reservation.recipient_price
        ?? reservation.recipient_price_time),
      reservation.price_currency ?? reservation.recipient_currency,
      lang,
    );
  const pendingReschedule = reservation.pending_reschedule;
  const canProposeReschedule = canProposeReservationReschedule(
    reservation,
    estimatedServerNowMs,
  );
  const canAcceptReschedule = canAcceptReservationReschedule(
    reservation,
    estimatedServerNowMs,
  );
  const cancellationAllowed = canCancelReservation(reservation);
  const actionsLocked = actionBusy || refreshing || failed;

  function timestamp(value: string) {
    const date = new Date(value);
    return `${date.toLocaleDateString(lang, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })} · ${date.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}`;
  }

  function actionError(error: unknown, fallbackKey = 'calendar.actionFailed') {
    if (error instanceof ApiError) {
      if (error.code === 'RESERVATION_REVISION_CONFLICT') {
        return t('calendar.reservationChanged');
      }
      if (error.code === 'RESERVATION_TIME_UNAVAILABLE') {
        return t('calendar.reservationUnavailable');
      }
      if (error.code === 'RESERVATION_START_IN_PAST') {
        return t('calendar.reservationPast');
      }
      if (error.code === 'RESERVATION_EXECUTION_LOCKED') {
        return t('calendar.reservationExecutionLocked');
      }
    }
    return t(fallbackKey);
  }

  async function runMutation(
    mutate: () => Promise<unknown>,
    mutationReachedCanonicalState: (canonical: Meetup) => boolean,
    fallbackKey = 'calendar.actionFailed',
  ) {
    if (actionBusyRef.current) return;
    actionBusyRef.current = true;
    setActionBusy(true);
    setShowReschedule(false);
    try {
      await mutate();
      await load(false);
    } catch (error) {
      // Ağ yanıtı kaybolmuş olsa bile sunucu mutasyonu commit etmiş olabilir.
      // Bu nedenle yalnız bilinen çatışmalarda değil her belirsiz sonuçta
      // kanonik kaydı yeniden oku. Okuma da başarısızsa `failed` eylemlerin
      // tamamını kapatır; kullanıcı çevrimdışı eski görünümü tekrar gönderemez.
      const canonical = await load(false);
      // Yalnız revizyon artışına bakmak yeterli değildir: başka cihaz ilgisiz
      // bir eylemle aynı revizyonu ilerletmiş olabilir. Ancak istenen semantik
      // sonuç kanonik kayıtta gerçekten oluşmuşsa hatayı tamamlanmış say.
      if (canonical && mutationReachedCanonicalState(canonical)) {
        return;
      }
      Alert.alert(t('common.error'), actionError(error, fallbackKey));
    } finally {
      actionBusyRef.current = false;
      setActionBusy(false);
    }
  }

  async function respond(action: 'accept' | 'decline') {
    if (action === 'accept') {
      const continueWithConflict = await confirmCalendarConflict(
        token,
        myId,
        new Date(reservation.proposed_at),
        reservation.duration_minutes,
        lang,
        t,
      );
      if (!continueWithConflict) return;
    }
    await runMutation(async () => {
      const response = await api.respondToMeetup(
        token,
        reservation.id,
        action,
        reservation.record_revision,
      );
      if (action === 'accept') {
        await syncAcceptedMeetup(myId, { ...reservation, ...response, status: 'accepted' }, {
          meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
          callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
          notes: t('calendar.systemEventNotes'),
        }).catch(() => {});
      }
    }, (canonical) => canonical.status === (action === 'accept' ? 'accepted' : 'declined'));
  }

  async function respondToReschedule(action: 'accept' | 'decline') {
    if (!pendingReschedule) return;
    if (action === 'accept') {
      const continueWithConflict = await confirmCalendarConflict(
        token,
        myId,
        new Date(pendingReschedule.proposed_at),
        reservation.duration_minutes,
        lang,
        t,
        { excludeMeetupId: reservation.id },
      );
      if (!continueWithConflict) return;
    }
    await runMutation(async () => {
      const response = await api.respondToMeetupReschedule(
        token,
        reservation.id,
        pendingReschedule.id,
        action,
        reservation.record_revision,
      );
      if (action === 'accept') {
        await syncAcceptedMeetup(myId, {
          ...reservation,
          ...response.meetup,
          pending_reschedule: null,
        }, {
          meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
          callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
          notes: t('calendar.systemEventNotes'),
        }).catch(() => {});
      }
    }, (canonical) => action === 'accept'
      ? canonical.status === 'accepted'
        && new Date(canonical.proposed_at).getTime()
          === new Date(pendingReschedule.proposed_at).getTime()
      : canonical.pending_reschedule?.id !== pendingReschedule.id
        && canonical.schedule_revision === reservation.schedule_revision
        && new Date(canonical.proposed_at).getTime()
          === new Date(reservation.proposed_at).getTime(),
    'calendar.rescheduleActionFailed');
  }

  async function withdrawReschedule() {
    if (!pendingReschedule) return;
    await runMutation(() => api.cancelMeetupReschedule(
      token,
      reservation.id,
      pendingReschedule.id,
      reservation.record_revision,
    ), (canonical) => canonical.pending_reschedule?.id !== pendingReschedule.id
      && new Date(canonical.proposed_at).getTime() === new Date(reservation.proposed_at).getTime(),
    'calendar.rescheduleActionFailed');
  }

  async function proposeReschedule() {
    if (!rescheduleDateValid) return;
    if (rescheduleDate.getTime() <= estimatedServerNowMs
      || Math.abs(rescheduleDate.getTime() - new Date(reservation.proposed_at).getTime()) < 60_000) {
      Alert.alert(t('common.error'), t('calendar.rescheduleFuture'));
      return;
    }
    const proposedAt = rescheduleDate.toISOString();
    await runMutation(() => api.proposeMeetupReschedule(
      token,
      reservation.id,
      proposedAt,
      reservation.record_revision,
    ), (canonical) => canonical.pending_reschedule?.direction === 'sent'
      && new Date(canonical.pending_reschedule.proposed_at).getTime()
        === new Date(proposedAt).getTime(),
    'calendar.rescheduleFailed');
  }

  function confirmCancellation() {
    const bodyKey = reservation.status === 'accepted'
      ? 'calendar.cancelConfirmationAcceptedBody'
      : 'calendar.cancelConfirmationPendingBody';
    Alert.alert(
      t('calendar.cancelConfirmationTitle'),
      t(bodyKey, { name: reservation.other_name }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('calendar.cancel'),
          style: 'destructive',
          onPress: () => runMutation(async () => {
            await api.cancelMeetup(token, reservation.id, reservation.record_revision);
            await removeMeetupFromSystemCalendar(myId, reservation.id).catch(() => 'unavailable' as const);
          }, (canonical) => canonical.status === 'cancelled'),
        },
      ],
      { cancelable: true },
    );
  }

  async function startReservedCall() {
    if (!onStartCall || actionBusyRef.current) return;
    actionBusyRef.current = true;
    setActionBusy(true);
    const current = await load(false);
    actionBusyRef.current = false;
    setActionBusy(false);
    if (current?.call_link_state === 'available') {
      onStartCall(current);
    }
  }

  function callLinkNotice() {
    if (reservation.call_link_state === 'too_early') return t('calendar.reservationCallTooEarly');
    if (reservation.call_link_state === 'expired') return t('calendar.reservationCallExpired');
    if (reservation.call_link_state === 'in_progress') return t('calendar.reservationCallInProgress');
    return null;
  }

  const notice = callLinkNotice();
  const durumSatiri = refreshing || (failed && meetup);
  const primaryActionStyle = [styles.actionButton, styles.primaryAction];
  const secondaryActionStyle = [styles.actionButton, styles.secondaryAction];

  return (
    <View style={styles.container}>
      {header}

      {/* Yenileme satırı kaydırılmıyor: başlıkla içerik arasında duruyor.
          Yüzen başlığın arkasında kalmaması için kendi üst boşluğunu taşıyor,
          ve göründüğünde kaydırma alanı zaten onun altından başladığı için
          boşluğu iki kez vermiyoruz. */}
      {durumSatiri && (
        <View style={[styles.refreshState, { paddingTop: ustBosluk }]}>
          <Text style={styles.refreshText}>
            {failed ? t('common.error') : t('common.loading')}
          </Text>
          {failed && (
            <CompactActionButton
              variant="plain"
              label={t('common.retry')}
              onPress={() => { load(false); }}
              style={styles.refreshRetry}
              testID="reservation-refresh-retry"
            />
          )}
        </View>
      )}

      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: durumSatiri ? spacing.md : ustBosluk + spacing.md },
          { paddingBottom: Math.max(insets.bottom, spacing.lg) + spacing.lg },
        ]}
        scrollIndicatorInsets={{ top: durumSatiri ? 0 : ustBosluk }}>
        <View style={styles.identity}>
          <Avatar photoUrl={meetup.other_photo} name={meetup.other_name} size={64} />
          <View style={styles.identityText}>
            <Text style={styles.name} numberOfLines={3}>{meetup.other_name}</Text>
            <Text style={styles.kind}>
              {t(meetup.kind === 'call' ? 'call.reservationLabel' : 'calendar.meetupLabel')}
            </Text>
              <Text style={styles.status}>{t(reservationStatusKey(meetup.status))}</Text>
            {outcomeKey(meetup.call_outcome) && (
              <Text style={styles.status} testID="reservation-detail-outcome">
                {t(outcomeKey(meetup.call_outcome) as string)}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.details}>
          <DetailRow label={t('calendar.reservationParticipant')} value={meetup.other_name}
            testID="reservation-detail-participant" styles={styles} />
          <DetailRow label={t('calendar.reservationDateTime')} value={reservationWhen}
            testID="reservation-detail-when" styles={styles} />
          <DetailRow label={t('calendar.reservationDuration')}
            value={`${formatDisplayCount(meetup.duration_minutes, lang)} ${t('calendar.minutesShort')}`}
            testID="reservation-detail-duration" styles={styles} />
          <DetailRow label={t('calendar.reservationRate')} value={rate}
            testID="reservation-detail-rate" styles={styles} />
          {!!meetup.location && (
            <DetailRow
              label={t(meetup.kind === 'call'
                ? 'calendar.reservationSubject'
                : 'calendar.reservationLocation')}
              value={meetup.location}
              testID="reservation-detail-place"
              styles={styles}
            />
          )}
          <DetailRow label={t('calendar.reservationQuotedTotal')} value={total}
            testID="reservation-detail-total" styles={styles} />
          {!!reservation.call_settlement && (
            <>
              <DetailRow
                label={t('calendar.reservationBilledDuration')}
                value={clock(reservation.call_settlement.billed_seconds)}
                testID="reservation-detail-billed-duration"
                styles={styles}
              />
              <DetailRow
                label={t(reservation.call_settlement.viewer_amount_kind === 'spent'
                  ? 'calendar.reservationSpentAmount'
                  : 'calendar.reservationEarnedAmount')}
                value={formatMoney(
                  Number(reservation.call_settlement.viewer_amount),
                  reservation.call_settlement.viewer_currency,
                  lang,
                )}
                testID="reservation-detail-settled-amount"
                styles={styles}
              />
              <DetailRow
                label={t('calendar.reservationSettledAt')}
                value={timestamp(reservation.call_settlement.settled_at)}
                testID="reservation-detail-settled-at"
                styles={styles}
              />
            </>
          )}
          {!!pendingReschedule && (
            <DetailRow
              label={t('calendar.reservationPendingReschedule')}
              value={formatNosubjectCalendarEventWhen({
                proposedAt: pendingReschedule.proposed_at,
                durationMinutes: meetup.duration_minutes,
                sourceTimeZone: pendingReschedule.source_time_zone,
              }, lang, deviceTimeZone)}
              testID="reservation-detail-reschedule"
              styles={styles}
            />
          )}
          <DetailRow label={t('calendar.reservationCreated')} value={timestamp(meetup.created_at)}
            styles={styles} />
          {!!meetup.accepted_at && (
            <DetailRow label={t('calendar.reservationAcceptedAt')}
              value={timestamp(meetup.accepted_at)} styles={styles} />
          )}
          {!!meetup.declined_at && (
            <DetailRow label={t('calendar.reservationDeclinedAt')}
              value={timestamp(meetup.declined_at)} styles={styles} />
          )}
          {!!meetup.cancelled_at && (
            <DetailRow label={t('calendar.reservationCancelledAt')}
              value={timestamp(meetup.cancelled_at)} testID="reservation-detail-cancelled-at"
              styles={styles} />
          )}
          {!!meetup.cancelled_at && meetup.cancelled_by_user_id != null && (
            <DetailRow label={t('calendar.reservationCancelledBy')}
              value={meetup.cancelled_by_user_id === myId
                ? t('calendar.reservationYou')
                : meetup.other_name}
              testID="reservation-detail-cancelled-by" styles={styles} />
          )}
          {!!meetup.cancellation_notification && (
            <DetailRow label={t('calendar.reservationCancellationNotification')}
              value={t(cancellationNotificationKey(meetup.cancellation_notification.state))}
              testID="reservation-detail-cancellation-notification" styles={styles} />
          )}
          {!meetup.accepted_at && !meetup.declined_at && !meetup.cancelled_at
            && !!meetup.responded_at && (
            <DetailRow label={t('calendar.reservationResponded')}
              value={timestamp(meetup.responded_at)} styles={styles} />
          )}
        </View>

        {notice && <Text style={styles.callNotice} testID="reservation-call-notice">{notice}</Text>}

        {showReschedule && canProposeReschedule && (
          <View style={styles.rescheduleEditor} testID="reservation-reschedule-editor">
            <Text style={styles.editorLabel}>{t('calendar.reservationNewTime')}</Text>
            <View style={styles.pickerInset}>
              <MeetupDateTimeField value={rescheduleDate} onChange={setRescheduleDate} onValidityChange={setRescheduleDateValid} />
            </View>
            <View style={styles.actionRow}>
              <TouchableOpacity
                accessibilityRole="button"
                disabled={actionsLocked}
                style={secondaryActionStyle}
                onPress={() => setShowReschedule(false)}>
                <Text style={styles.secondaryActionText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityState={{ disabled: actionsLocked || !rescheduleDateValid }}
                disabled={actionsLocked || !rescheduleDateValid}
                testID="reservation-submit-reschedule"
                style={primaryActionStyle}
                onPress={proposeReschedule}>
                <Text style={styles.primaryActionText}>{t('calendar.proposeReschedule')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={styles.actions} testID="reservation-actions">
          {actionBusy ? <ActivityIndicator color={colors.black} /> : !actionsLocked && (
            <>
              {meetup.status === 'pending' && meetup.direction === 'received' && (
                <View style={styles.actionRow}>
                  <TouchableOpacity accessibilityRole="button" testID="reservation-accept"
                    style={primaryActionStyle} onPress={() => respond('accept')}>
                    <Text style={styles.primaryActionText}>{t('calendar.accept')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity accessibilityRole="button" testID="reservation-decline"
                    style={secondaryActionStyle} onPress={() => respond('decline')}>
                    <Text style={styles.secondaryActionText}>{t('calendar.decline')}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {meetup.status === 'accepted' && pendingReschedule?.direction === 'received' && (
                <View style={styles.actionRow}>
                  {canAcceptReschedule && (
                    <TouchableOpacity accessibilityRole="button" testID="reservation-reschedule-accept"
                      style={primaryActionStyle} onPress={() => respondToReschedule('accept')}>
                      <Text style={styles.primaryActionText}>{t('calendar.accept')}</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity accessibilityRole="button" testID="reservation-reschedule-decline"
                    style={secondaryActionStyle} onPress={() => respondToReschedule('decline')}>
                    <Text style={styles.secondaryActionText}>{t('calendar.decline')}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {meetup.status === 'accepted' && pendingReschedule?.direction === 'sent' && (
                <TouchableOpacity accessibilityRole="button" testID="reservation-reschedule-withdraw"
                  style={secondaryActionStyle} onPress={withdrawReschedule}>
                  <Text style={styles.secondaryActionText}>{t('calendar.withdrawReschedule')}</Text>
                </TouchableOpacity>
              )}

              {meetup.kind === 'call'
                && meetup.status === 'accepted'
                && meetup.call_link_state === 'available'
                && callsEnabled
                && mediaAvailable
                && onStartCall && (
                  <TouchableOpacity accessibilityRole="button" testID="reservation-call-now"
                    style={primaryActionStyle} onPress={startReservedCall}>
                    <Text style={styles.primaryActionText}>{t('call.callNow')}</Text>
                  </TouchableOpacity>
                )}

              {canProposeReschedule && !showReschedule && (
                <TouchableOpacity
                  accessibilityRole="button"
                  testID="reservation-propose-reschedule"
                  style={secondaryActionStyle}
                  onPress={() => {
                    setRescheduleDate(initialRescheduleDate(meetup, estimatedServerNowMs));
                    setRescheduleDateValid(true);
                    setShowReschedule(true);
                  }}>
                  <Text style={styles.secondaryActionText}>{t('calendar.proposeNewTime')}</Text>
                </TouchableOpacity>
              )}

            </>
          )}
        </View>

        {/* 7.6 / D-668 — iptal ortalanmış kırmızı bir yazıydı; artık Çıkış ve
            Engelle gibi kendi bölümünde kırmızı bir satır. */}
        {!actionBusy && !actionsLocked && cancellationAllowed
          && !(meetup.status === 'accepted' && pendingReschedule?.direction === 'received') && (
            <View style={styles.cancelSection}>
              <SectionActionRow testID="reservation-cancel" label={t('calendar.cancel')} onPress={confirmCancellation} />
            </View>
          )}
      </ScrollView>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  loadState: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.md },
  loadError: { color: colors.gray400, fontSize: fontSize.md },
  refreshState: {
    minHeight: 36,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  refreshText: { color: colors.gray400, fontSize: fontSize.sm },
  // D-411 — bu kontrol hiç dolgu taşımadığı için dokunma hedefi yalnız metin
  // kadardı. Renk/kalınlık artık ortak bileşende; burada yalnız yatay dolgunun
  // sıfırlanması kalır, satır iki ucu ayrık düzenini korusun.
  refreshRetry: { paddingHorizontal: 0 },
  retryButton: {
    minHeight: 44,
    paddingHorizontal: spacing.lg,
    justifyContent: 'center',
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  content: { paddingHorizontal: spacing.lg, paddingTop: spacing.md },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingBottom: spacing.lg,
  },
  identityText: { flex: 1, minWidth: 0 },
  name: { color: colors.black, fontSize: fontSize.lg, fontWeight: '600' },
  kind: { color: colors.gray600, fontSize: fontSize.md, marginTop: 2 },
  status: { color: colors.gray400, fontSize: fontSize.sm, marginTop: 3 },
  details: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.gray200 },
  detailRow: {
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
    gap: spacing.xs,
  },
  detailLabel: { color: colors.gray400, fontSize: fontSize.sm },
  detailValue: { color: colors.black, fontSize: fontSize.md, lineHeight: 23 },
  callNotice: {
    color: colors.gray600,
    fontSize: fontSize.sm,
    lineHeight: 19,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  actions: { gap: spacing.sm, paddingTop: spacing.lg, alignItems: 'stretch' },
  actionRow: { flexDirection: 'row', gap: spacing.sm },
  actionButton: {
    flex: 1,
    minHeight: 48,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryAction: { backgroundColor: colors.black },
  primaryActionText: {
    color: colors.white,
    fontSize: fontSize.md,
    fontWeight: '600',
    textAlign: 'center',
  },
  secondaryAction: { borderWidth: 1, borderColor: colors.gray200 },
  secondaryActionText: {
    color: colors.black,
    fontSize: fontSize.md,
    fontWeight: '600',
    textAlign: 'center',
  },
  // Bölüm kendi 16'lık kenar boşluğunu taşır; sayfanın 24'lük iç boşluğu
  // üstüne binmesin, çerçeve yukarıdaki satırların kenarıyla hizalansın.
  cancelSection: { marginHorizontal: -16, marginTop: spacing.sm },
  rescheduleEditor: {
    paddingTop: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  editorLabel: {
    color: colors.black,
    fontSize: fontSize.md,
    fontWeight: '600',
    marginBottom: spacing.md,
  },
  pickerInset: { marginHorizontal: -spacing.lg },
});
